import { FC } from "react";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";

import { GamePlans, GamePlanEntity, Enterprise, EnterpriseAppState } from "@ctra/api";
import { useTranslation, Enterprise as Content } from "@ctra/i18n";
import { useGoogleAnalytics } from "@ctra/analytics";
import { Col, Grid, Row, Spin } from "@ctra/components";
import { isPending } from "@ctra/utils";

import { GAActions } from "@game-plans";
import { ScorecardContext } from "@scorecard";

import { GamePlanItem } from "../GamePlanItem";

/**
 * Game plan list entry
 * @param {string} id
 * @param {Pick<GamePlanEntity & {children?: React.ReactNode | undefined}, "dataDescriptorID" | "farmID" | "title" | "description" | "timePeriod" | "goal" | "taskList" | "unitSystem" | "children">} rest
 * @return {JSX.Element}
 * @constructor
 */
export const GamePlanEntry: FC<GamePlanEntity> = ({ id, ...rest }) => {
  const search = new URLSearchParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { trackEvent } = useGoogleAnalytics();
  const { md } = Grid.useBreakpoint();

  /**
   * Tell if the game plan is being deleted
   */
  const isDeleting = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, GamePlans.types.DELETE_GAME_PLAN, { primaryValue: id })
  );

  /**
   * Extract dataDescriptorID and farmID from ...rest
   * Feed into score card context provider
   */
  const { dataDescriptorID, farmID: _farmID } = rest;

  /**
   * Open the game plans modal or page
   */
  const handleClick = () => {
    const searchParams = new URLSearchParams(search);
    searchParams.append("gamePlanID", id.toString());
    searchParams.append("farmID", _farmID.toString());
    searchParams.append("dataDescriptorID", dataDescriptorID.toString());

    /* istanbul ignore next */
    trackEvent(GAActions.openGamePlan);

    Enterprise.history.push({
      pathname,
      search: searchParams.toString()
    });
  };

  return (
    <Row gutter={[md ? 0 : 16, 0]} align="middle" data-testid="game-plan-list-entry">
      <Col span={24}>
        <Spin spinning={isDeleting} tip={t<string>(Content.gamePlan.actions.delete.pending)}>
          <ScorecardContext.Provider dataDescriptorID={dataDescriptorID} farmID={_farmID}>
            <GamePlanItem id={id} onClick={handleClick} {...rest} />
          </ScorecardContext.Provider>
        </Spin>
      </Col>
    </Row>
  );
};
